import React, { useState } from 'react';
import {
    Box,
    Button,
    Container,
    Grid,
    Typography,
    Paper,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import FilterListIcon from '@mui/icons-material/FilterList';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import BarChartIcon from '@mui/icons-material/BarChart';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
} from 'recharts';

// ─── Data ─────────────────────────────────────────────────────────────────────

const revenueTrendData = [
    { date: '01 May', current: 3450780, previous: 3190000 },
    { date: '02 May', current: 3920450, previous: 3680000 },
    { date: '03 May', current: 2980560, previous: 3100000 },
    { date: '04 May', current: 2345780, previous: 2540000 },
    { date: '05 May', current: 4125890, previous: 3820000 },
    { date: '06 May', current: 4560230, previous: 4100000 },
    { date: '07 May', current: 3876450, previous: 3650000 },
    { date: '08 May', current: 4230100, previous: 3900000 },
    { date: '09 May', current: 5120300, previous: 4700000 },
    { date: '10 May', current: 4870650, previous: 4500000 },
    { date: '11 May', current: 5340200, previous: 4900000 },
    { date: '12 May', current: 6120450, previous: 5700000 },
    { date: '13 May', current: 5890780, previous: 5400000 },
    { date: '14 May', current: 5670340, previous: 5200000 },
    { date: '15 May', current: 6450890, previous: 5900000 },
    { date: '16 May', current: 6230120, previous: 5750000 },
    { date: '17 May', current: 6780450, previous: 6200000 },
    { date: '18 May', current: 6980450, previous: 6400000 },
    { date: '19 May', current: 6540230, previous: 6000000 },
    { date: '20 May', current: 5890670, previous: 5500000 },
    { date: '21 May', current: 5120340, previous: 4800000 },
    { date: '22 May', current: 5670890, previous: 5200000 },
    { date: '23 May', current: 6230450, previous: 5700000 },
    { date: '24 May', current: 6780120, previous: 6200000 },
    { date: '25 May', current: 6980450, previous: 6300000 },
    { date: '26 May', current: 6540230, previous: 5900000 },
    { date: '27 May', current: 5870450, previous: 5400000 },
    { date: '28 May', current: 5230120, previous: 4900000 },
    { date: '29 May', current: 4890670, previous: 4500000 },
    { date: '30 May', current: 4560340, previous: 4200000 },
    { date: '31 May', current: 4230890, previous: 3900000 },
];

const paymentChannelData = [
    { name: 'Cash (Onboard)', value: 25.1, amount: 31245600, color: '#1976d2', desc: 'Revenue collected in cash on bus' },
    { name: 'Online Booking', value: 27.7, amount: 34568200, color: '#2e7d32', desc: 'Revenue from online reservations' },
    { name: 'Online Onboard', value: 17.2, amount: 21456780, color: '#5c35a0', desc: 'Revenue from digital payments on bus' },
    { name: 'Travel Card', value: 11.7, amount: 14569120, color: '#ed6c02', desc: 'Revenue from travel card usage' },
    { name: 'Season Tickets', value: 10.6, amount: 13245300, color: '#00acc1', desc: 'Revenue from season ticket passes' },
    { name: 'Govt Pass', value: 7.6, amount: 9475850, color: '#78909c', desc: 'Revenue from government passes' },
];

const tableDateData = [
    { date: '01 May 2026', total: 3450780, cash: 875200, online: 1024560, onboard: 612300, travel: 389450, season: 312600, govt: 236670, vsLast: 8.2, up: true },
    { date: '02 May 2026', total: 3920450, cash: 965300, online: 1135780, onboard: 698900, travel: 412650, season: 348900, govt: 258920, vsLast: 6.7, up: true },
    { date: '03 May 2026', total: 2980560, cash: 745600, online: 892120, onboard: 525400, travel: 326800, season: 258300, govt: 232340, vsLast: 3.4, up: false },
    { date: '04 May 2026', total: 2345780, cash: 589200, online: 612340, onboard: 412560, travel: 268900, season: 236780, govt: 226000, vsLast: 7.6, up: false },
    { date: '05 May 2026', total: 4125890, cash: 1045600, online: 1256780, onboard: 689450, travel: 436200, season: 346780, govt: 351080, vsLast: 9.1, up: true },
    { date: '06 May 2026', total: 4560230, cash: 1125450, online: 1345600, onboard: 745230, travel: 489650, season: 367800, govt: 486500, vsLast: 12.3, up: true },
    { date: '07 May 2026', total: 3876450, cash: 965800, online: 1124340, onboard: 621450, travel: 412300, season: 324900, govt: 427660, vsLast: 5.8, up: true },
    { date: '08 May 2026', total: 4230100, cash: 1045200, online: 1230450, onboard: 698900, travel: 456300, season: 356700, govt: 442550, vsLast: 7.4, up: true },
    { date: '09 May 2026', total: 5120300, cash: 1289600, online: 1456780, onboard: 876540, travel: 523400, season: 456780, govt: 517200, vsLast: 11.2, up: true },
    { date: '10 May 2026', total: 4870650, cash: 1198400, online: 1367890, onboard: 823450, travel: 498600, season: 423400, govt: 558910, vsLast: 8.9, up: true },
    { date: '11 May 2026', total: 5340200, cash: 1356700, online: 1523400, onboard: 912300, travel: 567800, season: 478900, govt: 501100, vsLast: 13.1, up: true },
    { date: '12 May 2026', total: 6120450, cash: 1567800, online: 1789200, onboard: 1045600, travel: 623400, season: 534500, govt: 559950, vsLast: 15.6, up: true },
    { date: '13 May 2026', total: 5890780, cash: 1456700, online: 1678900, onboard: 989300, travel: 589600, season: 512300, govt: 663980, vsLast: 10.8, up: true },
    { date: '14 May 2026', total: 5670340, cash: 1389600, online: 1612300, onboard: 945600, travel: 567800, season: 489400, govt: 665640, vsLast: 9.3, up: true },
    { date: '15 May 2026', total: 6450890, cash: 1623400, online: 1845600, onboard: 1087600, travel: 645600, season: 567800, govt: 680890, vsLast: 17.2, up: true },
    { date: '16 May 2026', total: 6230120, cash: 1556700, online: 1756800, onboard: 1034500, travel: 623400, season: 545600, govt: 713120, vsLast: 14.5, up: true },
    { date: '17 May 2026', total: 6780450, cash: 1689300, online: 1923400, onboard: 1145600, travel: 678900, season: 589600, govt: 753650, vsLast: 19.3, up: true },
    { date: '18 May 2026', total: 6980450, cash: 1745600, online: 1989300, onboard: 1178900, travel: 698900, season: 612300, govt: 755450, vsLast: 20.8, up: true },
    { date: '19 May 2026', total: 6540230, cash: 1623400, online: 1856700, onboard: 1089300, travel: 656700, season: 578900, govt: 735230, vsLast: 16.7, up: true },
    { date: '20 May 2026', total: 5890670, cash: 1456700, online: 1678900, onboard: 989300, travel: 589600, season: 512300, govt: 663870, vsLast: 11.2, up: true },
    { date: '21 May 2026', total: 5120340, cash: 1289600, online: 1456780, onboard: 876540, travel: 523400, season: 456780, govt: 517240, vsLast: 5.6, up: true },
    { date: '22 May 2026', total: 5670890, cash: 1398700, online: 1612300, onboard: 945600, travel: 567800, season: 489400, govt: 657090, vsLast: 8.7, up: true },
    { date: '23 May 2026', total: 6230450, cash: 1556700, online: 1756800, onboard: 1034500, travel: 623400, season: 545600, govt: 713450, vsLast: 13.4, up: true },
    { date: '24 May 2026', total: 6780120, cash: 1689300, online: 1923400, onboard: 1145600, travel: 678900, season: 589600, govt: 753320, vsLast: 17.8, up: true },
    { date: '25 May 2026', total: 6980450, cash: 1745600, online: 1989300, onboard: 1178900, travel: 698900, season: 612300, govt: 755450, vsLast: 21.4, up: true },
    { date: '26 May 2026', total: 6540230, cash: 1623400, online: 1856700, onboard: 1089300, travel: 656700, season: 578900, govt: 735230, vsLast: 15.9, up: true },
    { date: '27 May 2026', total: 5870450, cash: 1456700, online: 1678900, onboard: 989300, travel: 589600, season: 512300, govt: 643650, vsLast: 10.3, up: true },
    { date: '28 May 2026', total: 5230120, cash: 1289600, online: 1456780, onboard: 876540, travel: 523400, season: 456780, govt: 627020, vsLast: 6.1, up: true },
    { date: '29 May 2026', total: 4890670, cash: 1198400, online: 1367890, onboard: 823450, travel: 498600, season: 423400, govt: 578930, vsLast: 4.2, up: true },
    { date: '30 May 2026', total: 4560340, cash: 1125450, online: 1345600, onboard: 745230, travel: 489650, season: 367800, govt: 486610, vsLast: 2.8, up: true },
    { date: '31 May 2026', total: 4230890, cash: 1045600, online: 1256780, onboard: 689450, travel: 436200, season: 346780, govt: 410080, vsLast: 1.5, up: true },
];

const fmtM = (v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v;
// const fmtRs = (v) => `Rs. ${v.toLocaleString()}`;

// ─── Stat Cards ───────────────────────────────────────────────────────────────

const topCards = [
    {
        label: 'Total Revenue (Current Period)',
        value: 'Rs. 124,560,850',
        sub: '▲ 12.5% vs Previous Period',
        up: true,
        color: '#1976d2',
        bg: '#e3f2fd',
        icon: <AttachMoneyIcon sx={{ fontSize: 22, color: '#1976d2' }} />,
    },
    {
        label: 'Avg. Daily Revenue',
        value: 'Rs. 4,152,028',
        sub: '▲ 8.3% vs Previous Period',
        up: true,
        color: '#2e7d32',
        bg: '#e8f5e9',
        icon: <ShowChartIcon sx={{ fontSize: 22, color: '#2e7d32' }} />,
    },
    {
        label: 'Highest Day Revenue',
        value: 'Rs. 6,980,450',
        sub: '25 May 2026',
        up: true,
        color: '#5c35a0',
        bg: '#ede7f6',
        icon: <ArrowUpwardIcon sx={{ fontSize: 22, color: '#5c35a0' }} />,
    },
    {
        label: 'Lowest Day Revenue',
        value: 'Rs. 2,345,780',
        sub: '04 May 2026',
        up: false,
        color: '#ed6c02',
        bg: '#fff3e0',
        icon: <ArrowDownwardIcon sx={{ fontSize: 22, color: '#ed6c02' }} />,
    },
    {
        label: 'Revenue Growth',
        value: '12.5%',
        sub: '▲ vs Previous Period',
        up: true,
        color: '#d32f2f',
        bg: '#ffebee',
        icon: <BarChartIcon sx={{ fontSize: 22, color: '#d32f2f' }} />,
    },
];

const RevenueTrendsPage = () => {
    const [period, setPeriod] = useState('Daily');
    const [depot, setDepot] = useState('All Depots');
    const [route, setRoute] = useState('All Routes');
    const [payChannel, setPayChannel] = useState('All Channels');

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(7);

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    const startIndex = page * rowsPerPage;

    // Totals for footer
    const totalRow = {
        total: tableDateData.reduce((s, r) => s + r.total, 0),
        cash: tableDateData.reduce((s, r) => s + r.cash, 0),
        online: tableDateData.reduce((s, r) => s + r.online, 0),
        onboard: tableDateData.reduce((s, r) => s + r.onboard, 0),
        travel: tableDateData.reduce((s, r) => s + r.travel, 0),
        season: tableDateData.reduce((s, r) => s + r.season, 0),
        govt: tableDateData.reduce((s, r) => s + r.govt, 0),
    };

    return (
        <Container component="main" maxWidth="full" sx={{ py: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>

                {/* ── Page Header ── */}
                <Box sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                    mt: 3,
                    flexWrap: 'wrap',
                    gap: 2,
                }}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Revenue Trends
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Button
                            variant="outlined"
                            startIcon={<CalendarTodayIcon />}
                            endIcon={<KeyboardArrowDownIcon />}
                            sx={{ borderRadius: '4px', textTransform: 'none', fontWeight: 600, height: 38, fontSize: 13 }}
                        >
                            01 May 2026 – 31 May 2026
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<FilterListIcon />}
                            sx={{ borderRadius: '4px', textTransform: 'none', fontWeight: 600, height: 38 }}
                        >
                            Filters
                        </Button>
                    </Box>
                </Box>

                {/* ── Filter Bar (full width grid) ── */}
                <Grid container sx={{ mb: 3, width: '100%' }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small" sx={{pr:2}}>
                            <InputLabel>Period</InputLabel>
                            <Select value={period} onChange={e => setPeriod(e.target.value)} label="Period">
                                <MenuItem value="Daily">Daily</MenuItem>
                                <MenuItem value="Weekly">Weekly</MenuItem>
                                <MenuItem value="Monthly">Monthly</MenuItem>
                                <MenuItem value="MTD">MTD</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small" sx={{pr:2}}>
                            <InputLabel>Depot</InputLabel>
                            <Select value={depot} onChange={e => setDepot(e.target.value)} label="Depot">
                                <MenuItem value="All Depots">All Depots</MenuItem>
                                <MenuItem value="Colombo Depot">Colombo Depot</MenuItem>
                                <MenuItem value="Kandy Depot">Kandy Depot</MenuItem>
                                <MenuItem value="Kurunegala Depot">Kurunegala Depot</MenuItem>
                                <MenuItem value="Matara Depot">Matara Depot</MenuItem>
                                <MenuItem value="Galle Depot">Galle Depot</MenuItem>
                                <MenuItem value="Negombo Depot">Negombo Depot</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small" sx={{pr:2}}>
                            <InputLabel>Route</InputLabel>
                            <Select value={route} onChange={e => setRoute(e.target.value)} label="Route">
                                <MenuItem value="All Routes">All Routes</MenuItem>
                                <MenuItem value="Route 001">Route 001 – Colombo Fort</MenuItem>
                                <MenuItem value="Route 002">Route 002 – Kandy Road</MenuItem>
                                <MenuItem value="Route 003">Route 003 – Galle Road</MenuItem>
                                <MenuItem value="Route 004">Route 004 – Negombo</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Payment Channel</InputLabel>
                            <Select value={payChannel} onChange={e => setPayChannel(e.target.value)} label="Payment Channel">
                                <MenuItem value="All Channels">All Channels</MenuItem>
                                <MenuItem value="Cash (Onboard)">Cash (Onboard)</MenuItem>
                                <MenuItem value="Online Booking">Online Booking</MenuItem>
                                <MenuItem value="Online Onboard">Online Onboard</MenuItem>
                                <MenuItem value="Travel Card">Travel Card</MenuItem>
                                <MenuItem value="Season Tickets">Season Tickets</MenuItem>
                                <MenuItem value="Govt Pass">Govt Pass</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                {/* ── Stat Cards (5 across) ── */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(5, 1fr)',
                    },
                    gap: 2,
                    width: '100%',
                    mb: 3,
                }}>
                    {topCards.map((card) => (
                        <Paper key={card.label} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Box sx={{ backgroundColor: card.bg, borderRadius: 1.5, p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    {card.icon}
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11, lineHeight: 1.3 }}>
                                    {card.label}
                                </Typography>
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: card.color, lineHeight: 1.2, mb: 0.5, fontSize: { xs: 16, md: 14, lg: 16 } }}>
                                {card.value}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {card.up
                                    ? <TrendingUpIcon sx={{ fontSize: 13, color: '#2e7d32' }} />
                                    : <TrendingDownIcon sx={{ fontSize: 13, color: '#d32f2f' }} />}
                                <Typography variant="caption" sx={{ color: card.up ? '#2e7d32' : '#d32f2f', fontWeight: 600, fontSize: 11 }}>
                                    {card.sub}
                                </Typography>
                            </Box>
                        </Paper>
                    ))}
                </Box>

                {/* ── Row 1: Revenue Trend Chart + Payment Channel Donut ── */}
                <Grid container sx={{ mb: 2, width: '100%' }}>

                    {/* 1. Revenue Trends Line Chart */}
                    <Grid item xs={12} md={8}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mr: { md: 2 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>1. Revenue Trends</Typography>
                                <FormControl size="small" sx={{ minWidth: 90 }}>
                                    <Select
                                        value={period}
                                        onChange={e => setPeriod(e.target.value)}
                                        sx={{ fontSize: 12, height: 28, '.MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' } }}
                                    >
                                        <MenuItem value="Daily">Daily</MenuItem>
                                        <MenuItem value="Weekly">Weekly</MenuItem>
                                        <MenuItem value="MTD">MTD</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                            <ResponsiveContainer width="100%" height={240}>
                                <LineChart data={revenueTrendData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={4} />
                                    <YAxis tickFormatter={fmtM} tick={{ fontSize: 10 }} />
                                    <Tooltip formatter={(v) => `Rs. ${(v / 1000000).toFixed(2)}M`} />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                    <Line type="monotone" dataKey="current" name="Total Revenue (Current Period)" stroke="#1976d2" strokeWidth={2} dot={{ r: 3 }} />
                                    <Line type="monotone" dataKey="previous" name="Previous Period" stroke="#90caf9" strokeWidth={1.5} strokeDasharray="5 5" dot={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>

                    {/* 2. Revenue Breakdown by Payment Channel Donut */}
                    <Grid item xs={12} md={4}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                2. Revenue Breakdown (by Payment Channel)
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Box sx={{ position: 'relative', width: 180, height: 180 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={paymentChannelData}
                                                cx="50%" cy="50%"
                                                innerRadius={55} outerRadius={82}
                                                dataKey="value"
                                                paddingAngle={2}
                                            >
                                                {paymentChannelData.map((entry, i) => (
                                                    <Cell key={i} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(v, name) => [`${v}%`, name]} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <Box sx={{
                                        position: 'absolute', top: '50%', left: '50%',
                                        transform: 'translate(-50%,-50%)', textAlign: 'center',
                                    }}>
                                        <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#555' }}>Rs.</Typography>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1976d2', lineHeight: 1 }}>124,560,850</Typography>
                                        <Typography sx={{ fontSize: 10, color: '#888' }}>Total Revenue</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ width: '100%', mt: 1 }}>
                                    {paymentChannelData.map((item) => (
                                        <Box key={item.name} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                                                <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: item.color, flexShrink: 0 }} />
                                                <Typography variant="caption">{item.name}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                <Typography variant="caption" color="text.secondary">Rs. {(item.amount / 1000000).toFixed(1)}M</Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: item.color, minWidth: 36, textAlign: 'right' }}>{item.value}%</Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

                {/* ── Row 2: Revenue Summary Table + Revenue Breakdown Legend (side by side) ── */}
                <Grid container sx={{ mb: 3, width: '100%' }}>

                    {/* 3. Revenue Summary by Date Table */}
                    <Grid item xs={12} md={8}>
                        <Paper variant="outlined" sx={{ borderRadius: 2 , mr: { md: 2 } }}>
                            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>3. Revenue Summary by Date</Typography>
                            </Box>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#7cdffa4b' }}>
                                            <TableCell sx={{ py: 1, fontSize: 12, fontWeight: 600 }}>Date</TableCell>
                                            <TableCell sx={{ py: 1, fontSize: 12, fontWeight: 600 }} align="right">Total Revenue (Rs.)</TableCell>
                                            <TableCell sx={{ py: 1, fontSize: 12, fontWeight: 600 }} align="right">Cash (Onboard)</TableCell>
                                            <TableCell sx={{ py: 1, fontSize: 12, fontWeight: 600 }} align="right">Online Booking</TableCell>
                                            <TableCell sx={{ py: 1, fontSize: 12, fontWeight: 600 }} align="right">Online Onboard</TableCell>
                                            <TableCell sx={{ py: 1, fontSize: 12, fontWeight: 600 }} align="right">Travel Card</TableCell>
                                            <TableCell sx={{ py: 1, fontSize: 12, fontWeight: 600 }} align="right">Season Tickets</TableCell>
                                            <TableCell sx={{ py: 1, fontSize: 12, fontWeight: 600 }} align="right">Govt Pass</TableCell>
                                            <TableCell sx={{ py: 1, fontSize: 12, fontWeight: 600 }} align="right">vs Previous Period</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {tableDateData
                                            .slice(startIndex, startIndex + rowsPerPage)
                                            .map((row, i) => (
                                                <TableRow key={i} hover>
                                                    <TableCell sx={{ py: 0.5, fontSize: 12 }}>{row.date}</TableCell>
                                                    <TableCell sx={{ py: 0.5, fontSize: 12, fontWeight: 600, color: '#1976d2' }} align="right">{row.total.toLocaleString()}</TableCell>
                                                    <TableCell sx={{ py: 0.5, fontSize: 12 }} align="right">{row.cash.toLocaleString()}</TableCell>
                                                    <TableCell sx={{ py: 0.5, fontSize: 12 }} align="right">{row.online.toLocaleString()}</TableCell>
                                                    <TableCell sx={{ py: 0.5, fontSize: 12 }} align="right">{row.onboard.toLocaleString()}</TableCell>
                                                    <TableCell sx={{ py: 0.5, fontSize: 12 }} align="right">{row.travel.toLocaleString()}</TableCell>
                                                    <TableCell sx={{ py: 0.5, fontSize: 12 }} align="right">{row.season.toLocaleString()}</TableCell>
                                                    <TableCell sx={{ py: 0.5, fontSize: 12 }} align="right">{row.govt.toLocaleString()}</TableCell>
                                                    <TableCell sx={{ py: 0.5, fontSize: 12 }} align="right">
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.3 }}>
                                                            {row.up
                                                                ? <TrendingUpIcon sx={{ fontSize: 13, color: '#2e7d32' }} />
                                                                : <TrendingDownIcon sx={{ fontSize: 13, color: '#d32f2f' }} />}
                                                            <Typography variant="caption" sx={{ color: row.up ? '#2e7d32' : '#d32f2f', fontWeight: 600 }}>
                                                                {row.up ? '▲' : '▼'} {row.vsLast}%
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}

                                        {/* Total / Avg Row */}
                                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                            <TableCell sx={{ py: 1, fontSize: 12, fontWeight: 700 }}>Total / Avg</TableCell>
                                            <TableCell sx={{ py: 1, fontSize: 12, fontWeight: 700, color: '#1976d2' }} align="right">{totalRow.total.toLocaleString()}</TableCell>
                                            <TableCell sx={{ py: 1, fontSize: 12, fontWeight: 700, color: '#1976d2' }} align="right">{totalRow.cash.toLocaleString()}</TableCell>
                                            <TableCell sx={{ py: 1, fontSize: 12, fontWeight: 700, color: '#1976d2' }} align="right">{totalRow.online.toLocaleString()}</TableCell>
                                            <TableCell sx={{ py: 1, fontSize: 12, fontWeight: 700, color: '#1976d2' }} align="right">{totalRow.onboard.toLocaleString()}</TableCell>
                                            <TableCell sx={{ py: 1, fontSize: 12, fontWeight: 700, color: '#1976d2' }} align="right">{totalRow.travel.toLocaleString()}</TableCell>
                                            <TableCell sx={{ py: 1, fontSize: 12, fontWeight: 700, color: '#1976d2' }} align="right">{totalRow.season.toLocaleString()}</TableCell>
                                            <TableCell sx={{ py: 1, fontSize: 12, fontWeight: 700, color: '#1976d2' }} align="right">{totalRow.govt.toLocaleString()}</TableCell>
                                            <TableCell sx={{ py: 1, fontSize: 12, fontWeight: 700, color: '#2e7d32' }} align="right">▲ 12.5%</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                showFirstButton
                                showLastButton
                                component="div"
                                count={tableDateData.length}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[7, 10, 25, 50]}
                            />
                        </Paper>
                    </Grid>

                    {/* Revenue Breakdown Legend Panel */}
                    <Grid item xs={12} md={4}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                                Revenue Breakdown – Legend
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {paymentChannelData.map((item) => (
                                    <Box key={item.name}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{
                                                    width: 32, height: 32, borderRadius: '6px',
                                                    backgroundColor: item.color + '22',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}>
                                                    <Box sx={{ width: 14, height: 14, borderRadius: '3px', backgroundColor: item.color }} />
                                                </Box>
                                                <Box>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{item.name}</Typography>
                                                    <Typography variant="caption" color="text.secondary">{item.desc}</Typography>
                                                </Box>
                                            </Box>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: item.color }}>{item.value}%</Typography>
                                        </Box>
                                        {/* colored progress bar */}
                                        <Box sx={{ height: 4, borderRadius: 2, backgroundColor: '#f0f0f0', mt: 0.5 }}>
                                            <Box sx={{ height: '100%', borderRadius: 2, backgroundColor: item.color, width: `${item.value / 30 * 100}%` }} />
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

            </Box>
        </Container>
    );
};

export default RevenueTrendsPage;